<template>
  <el-dialog ref="dialog" :visible="visible" title="删除图片" width="min(520px, calc(100vw - 24px))" append-to-body custom-class="image-resource-delete-dialog" :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close" @opened="focusCancel" @closed="$emit('closed')" @keydown.tab.native="trapFocus">
    <div class="image-delete-body" :aria-busy="String(loading || saving)">
      <p v-if="loading" role="status">正在读取图片信息…</p>
      <template v-if="image">
        <p><i class="el-icon-warning-outline" aria-hidden="true"></i> 确认删除“{{ image.name }}”（v{{ image.version }}）？</p>
        <p>删除后将从图片资源库隐藏，不能再从库中选择。历史版本和服务器文件保留，已使用该图片的大屏、模板不会丢图。</p>
        <p>“恢复上一版”仅恢复图片内容，不用于撤销本次删除。</p>
      </template>
      <p v-if="error" class="image-delete-error" role="alert">{{ error }}</p>
      <el-button v-if="!image && error && !loading && !blocked" size="small" @click="load">重新读取图片信息</el-button>
      <p v-if="saving" role="status">正在删除，请勿重复提交…</p>
    </div>
    <span slot="footer" class="image-delete-actions"><el-button ref="cancel" :disabled="saving" @click="close">{{ blocked ? '关闭并刷新列表' : '取消' }}</el-button><el-button type="danger" :loading="saving" :disabled="!image || loading || saving || blocked || !canDelete" @click="remove">确认删除</el-button></span>
  </el-dialog>
</template>
<script>
import store from '@/store'
import { getImage, archiveImage } from '@/api/image-resources'
import { deletableImage, archivedImage, imageError } from '@/utils/image-resources'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'

export default {
  props: { visible: Boolean, imageId: { type: String, required: true } },
  data: () => ({ image: null, loading: false, saving: false, blocked: false, error: '' }),
  computed: {
    canDelete () { return store.getters['auth/hasResourcePermission']('visualizationImage:query') && store.getters['auth/hasResourcePermission']('visualizationImage:delete') }
  },
  watch: {
    visible (value) { if (value) this.load(); else this.cancelRead() },
    imageId () { this.cancelRead(); this.image = null; if (this.visible) this.load() },
    canDelete (value) { if (!value) { this.cancelRead(); this.$emit('update:visible', false) } }
  },
  created () { this._read = 0; if (this.visible) this.load() },
  mounted () { if (this.visible) this.$nextTick(this.focusCancel) },
  beforeDestroy () { this.cancelRead() },
  methods: {
    cancelRead () { this._read++; if (this._cancel) this._cancel.cancel() },
    focusCancel () { if (!this._isDestroyed && this.$refs.cancel) this.$refs.cancel.$el.focus() },
    trapFocus (event) {
      if (!this.$refs.dialog) return
      const controls = Array.from(this.$refs.dialog.$el.querySelectorAll('button:not([disabled]), [tabindex="0"]')).filter(el => el.getClientRects().length)
      if (!controls.length) { event.preventDefault(); return }
      const first = controls[0], last = controls[controls.length - 1]
      if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus() }
    },
    close () {
      if (this.saving) return
      this.cancelRead(); this.$emit('update:visible', false)
      if (this.blocked) this.$emit('check')
    },
    async load () {
      if (this.saving || this.blocked) return
      this.cancelRead(); const request = this._read, id = this.imageId
      this.image = null; this.error = ''
      if (!this.visible || !this.canDelete) return
      this._cancel = createRequestCancellation(); this.loading = true
      try {
        const image = deletableImage(await getImage(id, { cancelToken: this._cancel.token }), id)
        if (request !== this._read || this._isDestroyed || !this.visible || !this.canDelete || id !== this.imageId) return
        this.image = image
      } catch (error) {
        if (request === this._read && !this._isDestroyed && !isRequestCanceled(error)) this.error = error.status === 404 ? '图片已删除或不可访问，请关闭并刷新列表核对。' : imageError(error)
      } finally { if (request === this._read && !this._isDestroyed) this.loading = false }
    },
    async remove () {
      if (!this.image || this.image.id !== this.imageId || this.loading || this.saving || this.blocked || !this.visible || !this.canDelete) return
      const image = this.image, id = this.imageId, request = this._read
      this.saving = true; this.error = ''
      try {
        const response = await archiveImage(id, { revision: image.revision })
        this.blocked = true
        const item = archivedImage(response, image)
        if (request !== this._read || this._isDestroyed || !this.visible || !this.canDelete || id !== this.imageId) return
        this.blocked = false; this.$emit('deleted', item); this.$emit('update:visible', false)
      } catch (error) {
        if (request !== this._read || this._isDestroyed || !this.visible || !this.canDelete || id !== this.imageId) return
        // 冲突、权限变化和未知回执不自动重发，必须刷新后重新确认对象。
        this.blocked = this.blocked || !error.status || error.status >= 500 || [401, 403, 404, 409].includes(error.status)
        this.error = this.blocked ? (error.status === 409 ? '图片已发生变化，本次未确认删除；请关闭并刷新列表重新核对。' : error.status === 404 ? '图片不可访问或删除接口尚未部署，请关闭并刷新列表核对，确认后端已更新。' : '删除结果或权限未确认，请关闭并刷新列表检查，勿重复提交。') : imageError(error)
        if (this.blocked) this.$nextTick(this.focusCancel)
      } finally { if (!this._isDestroyed) this.saving = false }
    }
  }
}
</script>
<style>
.image-resource-delete-dialog { background: #fff !important; color: #1f2937; border-radius: 6px; }
.image-resource-delete-dialog .el-dialog__title, .image-resource-delete-dialog .el-dialog__body { color: #1f2937; }
.image-resource-delete-dialog .image-delete-body { max-height: 50vh; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: none; -ms-overflow-style: none; }
.image-resource-delete-dialog .image-delete-body::-webkit-scrollbar { display: none; }
.image-resource-delete-dialog p { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.7; }
.image-resource-delete-dialog .image-delete-error { color: #b42318; }
.image-resource-delete-dialog .image-delete-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; padding-bottom: env(safe-area-inset-bottom); }
.image-resource-delete-dialog .el-button { min-height: 44px; margin-left: 0; border-radius: 4px; }
.image-resource-delete-dialog .el-button--danger { color: #fff; background: #b42318; border-color: #b42318; }
.image-resource-delete-dialog .el-button--default { color: #1f2937; background: #fff; border-color: #dcdfe6; }
</style>
